/**
 * CSV and TSV.
 *
 * Round-tripping CSV losslessly means remembering more than the cell values.
 * `a,b` and `"a","b"` parse to the same rows but are different files, and a
 * viewer that re-quotes everything on save turns a one-cell edit into a
 * whole-file diff. So the parser records which fields were quoted and the
 * serialiser reproduces that exactly (§13, §24).
 *
 * Cells beginning with `=`, `+`, `-` or `@` are the CSV injection vector: a
 * spreadsheet opening the file would execute them. This handler treats every
 * cell as literal text — §15 forbids evaluating them — and flags their presence
 * so the export path can offer escaping.
 */
import { decodeTextBytes, encodeTextForSave } from '@moduviewer/core';
import type { Encoding, LineEnding } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';

export interface CsvModel {
  readonly rows: readonly (readonly string[])[];
  /** Which fields were quoted in the source, so saving can reproduce them. */
  readonly quoted: readonly (readonly boolean[])[];
  readonly delimiter: string;
  readonly lineEnding: LineEnding;
  readonly sourceEncoding: Encoding;
  readonly hadBom: boolean;
  readonly endsWithNewline: boolean;
  /** Cells whose leading character would be executed by a spreadsheet (§15). */
  readonly injectionRisks: number;
}

const PREVIEW_ROWS = 100;
const PREVIEW_BYTES = 128 * 1024;
const INJECTION_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

/** Candidate delimiters, most likely first. */
const DELIMITERS = [',', '\t', ';', '|'] as const;

/**
 * Pick a delimiter by which candidate yields the most consistent column count
 * across the first few lines.
 *
 * Counting raw occurrences is not enough: a text column full of commas would
 * beat a genuine tab delimiter. Consistency is the signal that actually
 * distinguishes a delimiter from ordinary punctuation.
 */
export function detectDelimiter(sample: string, fallback = ','): string {
  const lines = sample.split(/\r\n|\r|\n/).filter((l) => l.length > 0).slice(0, 20);
  if (lines.length === 0) return fallback;

  let best = fallback;
  let bestScore = -1;

  for (const delimiter of DELIMITERS) {
    const counts = lines.map((line) => parseLineFieldCount(line, delimiter));
    const first = counts[0]!;
    if (first < 2) continue;
    const consistent = counts.filter((c) => c === first).length;
    // Consistency dominates; field count breaks ties toward the delimiter that
    // actually splits the data rather than one that appears once per line.
    const score = consistent * 100 + first;
    if (score > bestScore) { bestScore = score; best = delimiter; }
  }
  return best;
}

/** Field count for one line, respecting quotes. */
function parseLineFieldCount(line: string, delimiter: string): number {
  let count = 1;
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') i++;
        else inQuotes = false;
      }
    } else if (ch === '"') inQuotes = true;
    else if (ch === delimiter) count++;
  }
  return count;
}

export interface ParsedCsv {
  readonly rows: string[][];
  readonly quoted: boolean[][];
  readonly endsWithNewline: boolean;
}

/**
 * RFC 4180 parse that also records quoting.
 *
 * A quoted empty final field is why `endsWithNewline` is tracked separately
 * from "the last row is empty" — the two are different files and the
 * distinction is invisible in the row array alone.
 */
export function parseCsv(text: string, delimiter: string): ParsedCsv {
  const rows: string[][] = [];
  const quoted: boolean[][] = [];
  let row: string[] = [];
  let rowQuoted: boolean[] = [];
  let field = '';
  let fieldWasQuoted = false;
  let inQuotes = false;
  let sawAnyChar = false;

  const endField = () => {
    row.push(field);
    rowQuoted.push(fieldWasQuoted);
    field = '';
    fieldWasQuoted = false;
  };
  const endRow = () => {
    endField();
    rows.push(row);
    quoted.push(rowQuoted);
    row = [];
    rowQuoted = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    sawAnyChar = true;

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += ch;
      continue;
    }

    if (ch === '"' && field === '') { inQuotes = true; fieldWasQuoted = true; continue; }
    if (ch === delimiter) { endField(); continue; }
    if (ch === '\r') {
      if (text[i + 1] === '\n') i++;
      endRow();
      continue;
    }
    if (ch === '\n') { endRow(); continue; }
    field += ch;
  }

  const endsWithNewline = sawAnyChar && field === '' && row.length === 0 && !fieldWasQuoted;
  if (!endsWithNewline && sawAnyChar) endRow();

  return { rows, quoted, endsWithNewline };
}

/** True when a field must be quoted to survive a round trip. */
function needsQuoting(value: string, delimiter: string): boolean {
  return value.includes(delimiter) || value.includes('"')
    || value.includes('\n') || value.includes('\r');
}

export interface SerializeOptions {
  /** §15 — prefix risky cells with a quote so spreadsheets treat them as text. */
  readonly escapeInjection?: boolean;
}

export function serializeCsv(model: CsvModel, options: SerializeOptions = {}): string {
  const eol = model.lineEnding === 'crlf' ? '\r\n'
    : model.lineEnding === 'cr' ? '\r' : '\n';

  const lines = model.rows.map((row, r) => row.map((value, c) => {
    let out = value;
    if (options.escapeInjection && isInjectionRisk(out)) out = `'${out}`;
    const wasQuoted = model.quoted[r]?.[c] ?? false;
    if (wasQuoted || needsQuoting(out, model.delimiter)) {
      return `"${out.replace(/"/g, '""')}"`;
    }
    return out;
  }).join(model.delimiter));

  return lines.join(eol) + (model.endsWithNewline ? eol : '');
}

export function isInjectionRisk(value: string): boolean {
  return value.length > 0 && INJECTION_PREFIXES.includes(value[0]!);
}

function countInjectionRisks(rows: readonly (readonly string[])[]): number {
  let n = 0;
  for (const row of rows) for (const cell of row) if (isInjectionRisk(cell)) n++;
  return n;
}

function delimiterFor(formatId: string, sample: string): string {
  if (formatId === 'tsv') return '\t';
  return detectDelimiter(sample);
}

export const csvHandler: FormatHandler<CsvModel> = {
  id: 'csv',
  formatIds: ['csv', 'tsv'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const head = source.bytes.subarray(0, Math.min(source.bytes.length, PREVIEW_BYTES));
    const decoded = decodeTextBytes(head);
    const delimiter = delimiterFor(source.formatId, decoded.text);
    const parsed = parseCsv(decoded.text, delimiter);

    // The window rarely ends on a row boundary; the final row of a truncated
    // sample is a fragment, not data.
    const truncated = source.bytes.length > head.length;
    const rows = truncated ? parsed.rows.slice(0, -1) : parsed.rows;

    return {
      kind: 'table',
      unitCount: truncated ? undefined : rows.length,
      unitKey: undefined,
      firstContent: rows.slice(0, PREVIEW_ROWS),
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<CsvModel>> {
    throwIfAborted(ctx.signal);
    const decoded = decodeTextBytes(source.bytes);
    const delimiter = delimiterFor(source.formatId, decoded.text.slice(0, 8192));
    const parsed = parseCsv(decoded.text, delimiter);

    const cellCount = parsed.rows.reduce((n, r) => n + r.length, 0);
    if (cellCount > ctx.limits.maxSpreadsheetCells) {
      throw new HandlerError(
        'error.unableToRead',
        `${cellCount} cells exceeds the limit of ${ctx.limits.maxSpreadsheetCells}`,
      );
    }

    const injectionRisks = countInjectionRisks(parsed.rows);
    const model: CsvModel = {
      rows: parsed.rows,
      quoted: parsed.quoted,
      delimiter,
      lineEnding: decoded.lineEnding === 'none' ? 'lf' : decoded.lineEnding,
      sourceEncoding: decoded.detection.encoding,
      hadBom: decoded.detection.hasBom,
      endsWithNewline: parsed.endsWithNewline,
      injectionRisks,
    };

    const warnings: HandlerWarning[] = [];
    if (injectionRisks > 0) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'caution',
        detail: `${injectionRisks} cell(s) begin with a formula character. They are shown as text and never evaluated (§15).`,
      });
    }
    if (model.sourceEncoding !== 'utf-8') {
      warnings.push({
        messageKey: 'error.formattingMayNotBePreserved',
        severity: 'caution',
        detail: `saved as UTF-8; the original was ${model.sourceEncoding}`,
      });
    }

    return {
      formatId: source.formatId,
      model,
      readOnly: ctx.assessment.forceReadOnly,
      editLevel: 'full-edit',
      lossOnSave: model.sourceEncoding === 'utf-8' ? [] : [
        `저장 시 UTF-8로 변환됩니다 (원본 인코딩: ${model.sourceEncoding})`,
      ],
      warnings,
    };
  },

  async exportBytes(
    model: CsvModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const text = serializeCsv(model);
    return {
      bytes: encodeTextForSave(text, model.lineEnding, {
        withBom: model.hadBom && model.sourceEncoding === 'utf-8',
      }),
      suggestedName: source.filename,
      formatId: source.formatId,
      dropped: model.sourceEncoding === 'utf-8' ? [] : [`original ${model.sourceEncoding} encoding`],
    };
  },

  async validateExport(
    bytes: Uint8Array, model: CsvModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const decoded = decodeTextBytes(bytes);
      const reparsed = parseCsv(decoded.text, model.delimiter);
      reopened = true;

      if (reparsed.rows.length !== model.rows.length) {
        issues.push(`row count changed: ${model.rows.length} → ${reparsed.rows.length}`);
      } else {
        const mismatch = model.rows.findIndex((row, r) => {
          const other = reparsed.rows[r]!;
          return row.length !== other.length || row.some((cell, c) => cell !== other[c]);
        });
        if (mismatch >= 0) issues.push(`row ${mismatch + 1} differs after reopening`);
      }
      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the exported file: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/** Replace a cell, keeping quoting metadata aligned with the new value. */
export function withCell(model: CsvModel, row: number, col: number, value: string): CsvModel {
  const rows = model.rows.map((r, i) => (i === row ? r.map((c, j) => (j === col ? value : c)) : r));
  return { ...model, rows, injectionRisks: countInjectionRisks(rows) };
}
